/* eslint-disable react/no-unknown-property */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import {
  Action,
  Card, CardProps, FontWeight, HStack, Icon, ICON_CHECK, ICON_CHEVRON, ICON_CLOSE, ICON_PLUS, ICON_STAR_OUTLINE, Stack, Text,
  useToggle,
} from '@doveit/bricks';
import { CardHeaderProps } from '@doveit/bricks/dist/typings/components/card/composition';
import { AgentReview } from '../../../providers/api/dtos';
import ReviewAvatar from '../../../review/components/ReviewAvatar/ReviewAvatar';
import { splitTextToNearestString } from '../../../utils/text/text';
import * as styles from './AgentReviewSelectablePreview.style';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';

type AgentReviewSelectablePreviewOperation = 'add' | 'remove';

const mapIconToOperation: { [key in AgentReviewSelectablePreviewOperation]: string } = {
  add: ICON_PLUS,
  remove: ICON_CLOSE,
};

export interface AgentReviewSelectablePreviewBaseProps extends React.AriaAttributes {
  review: AgentReview,
  maxLength?: number,
  asContent?: CardProps['asContent'],
}

export type AgentReviewSelectablePreviewProps = AgentReviewSelectablePreviewBaseProps & ({
  operation?: AgentReviewSelectablePreviewOperation,
  onClick?: VoidFunction,
  selected?: boolean,
  disabled?: boolean,
  index?: number,
  primaryActions?: undefined,
  secondaryActions?: undefined,
} | {
  operation?: undefined,
  onClick?: undefined,
  selected?: undefined,
  disabled?: undefined,
  index?: undefined,
  primaryActions?: CardHeaderProps['primaryActions'],
  secondaryActions?: CardHeaderProps['secondaryActions'],
});

const AgentReviewSelectablePreview: React.FC<AgentReviewSelectablePreviewProps> = ({
  review,
  operation,
  asContent,
  onClick,
  selected = false,
  disabled = false,
  index,
  maxLength = 300,
  'aria-label': ariaLabel = 'agent-review-selectable-preview',
  primaryActions,
  secondaryActions,
  ...rest
}) => {
  const { opened, toggle } = useToggle(false);

  const reviewText = React.useMemo(
    () => splitTextToNearestString(review.text, '.', maxLength),
    [maxLength, review.text],
  );

  const interactive = React.useMemo(
    () => !!onClick,
    [onClick],
  );

  const active = React.useMemo(
    () => selected && !disabled,
    [selected, disabled],
  );

  return (
    <div
      {...rest}
      css={styles.basic}
      $disabled={disabled}
      $interactive={interactive}
      $operation={operation}
      aria-label={ariaLabel}
    >
      <Card
        onClick={(!disabled && onClick) ? onClick : undefined}
        highlighted={active}
        aria-label={`${ariaLabel} (card)`}
        paddingSize="XS"
        asContent={asContent}
      >
        <Card.Header
          primaryActions={[
            <HStack
              gap={100}
              alignItems="center"
              wrap="nowrap"
            >
              {typeof index === 'number' && (
                <div
                  css={styles.index}
                  aria-label={`${ariaLabel} (index)`}
                  $active={active}
                >
                  {index}
                </div>
              )}
              {selected && (
                <div
                  css={styles.icon}
                  $active={active}
                  className="statusIcon"
                  aria-label="Icona stato"
                >
                  <Icon
                    size={18}
                    path={ICON_CHECK}
                  />
                </div>
              )}
              {operation && interactive && (
                <div
                  css={styles.icon}
                  $active={active}
                  className="operationIcon"
                  aria-label="Icona operazione"
                >
                  <Icon
                    size={18}
                    path={mapIconToOperation[operation]}
                  />
                </div>
              )}
            </HStack>,
            ...(primaryActions || []),
          ]}
          secondaryActions={secondaryActions}
        >
          <HStack gap={100} wrap="nowrap">
            <ReviewAvatar size="S" />
            <div>
              <Text.BodySmall
                fontWeight={FontWeight.MEDIUM}
                css={setVerticalEllipsis(1)}
              >
                {review.author}
              </Text.BodySmall>
              <HStack gap={75} wrap="nowrap">
                <HStack gap={0} wrap="nowrap">
                  <Text.Caption>
                    {review.stars}
                  </Text.Caption>
                  <Icon
                    size={12}
                    path={ICON_STAR_OUTLINE}
                  />
                </HStack>
                <Text.Caption css={setVerticalEllipsis(1)}>
                  {review.title}
                </Text.Caption>
              </HStack>
            </div>
          </HStack>
        </Card.Header>
        <Card.Content>
          {(reviewText.length === 1 || disabled) && (
            <div
              css={styles.text}
            >
              {reviewText[0]}
            </div>
          )}
          {reviewText.length === 2 && !disabled && (
            <div
              css={styles.text}
              onClick={(e) => e.stopPropagation()}
            >
              <Stack gap={150}>
                {opened && review.text}
                {!opened && (reviewText.length > 1 ? `${reviewText[0]} [...]` : reviewText[0])}
                {reviewText.length > 1 && (
                  <div>
                    <Action
                      label={opened ? 'Nascondi' : 'Mostra'}
                      aria-label={`Azione per ${opened ? 'nascondere' : 'mostrare'} il testo`}
                      size="XS"
                      color="neutral"
                      onClick={toggle}
                      iconRight={{ path: ICON_CHEVRON, direction: opened ? 'top' : 'bottom' }}
                    />
                  </div>
                )}
              </Stack>
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default AgentReviewSelectablePreview;
